"use client";

/** @format */
import type React from "react";
import { useMemo, useState, useCallback, useEffect, useTransition, useRef } from "react";
import { Sidebar, SidebarContent } from "./ui/sidebar";
import { ScrollArea } from "./ui/scroll-area";
import { useSession } from "../context/SessionContext";
import { useTranslation } from "react-i18next";
import { useNotificationData } from "../hooks/useNotificationData";
import { useUserHistory } from "../hooks/useUserHistory";
import { useTransferActions } from "../hooks/useTransferActions";
import { useNotificationHandlers } from "../hooks/useNotificationHandlers";
import { useUserFiltering } from "../hooks/useUserFiltering";
import { useViewToggle } from "../hooks/useViewToggle";
import { useRelativeTime } from "../hooks/useRelativeTime";
import { useNotificationEffects } from "../hooks/useNotificationEffects";
import { SidebarToggleHeader } from "./SidebarToggleHeader";
import { SidebarFilters } from "./SidebarFilters";
import { UserHistoryList } from "./UserHistoryList";
import { RecentUsersList } from "./RecentUserList";
import { NotificationsList } from "./NotificationList";
import { SidebarSubViewBar } from "./SidebarSubViewBar";
import { getAgentAbbreviation } from "../lib/chatUtils";
import { getBulkMessageHistory } from "../lib/messageHistoryClient";
import { groupMessagesIntoConversations } from "../lib/chatUtils";

type SubView = "new" | "in_process" | "agent";
type FilterKind = "all" | "agent" | "insurance" | "date";

interface ChatSidebarProps {
  onSelectUser: (phone: string, userId?: string | number) => void;
  usersWithHistory: Set<string>;
  loading: boolean;
  updateUsersWithHistory: React.Dispatch<React.SetStateAction<Set<string>>>;
  conversationCounts?: { [phone: string]: number };
  setConversationCounts?: React.Dispatch<React.SetStateAction<{ [phone: string]: number }>>;
  dateFilter: { mode: "single" | "range"; from?: string; to?: string };
  onDateFilterChange: (filter: { mode: "single" | "range"; from?: string; to?: string }) => void;
}

declare global {
  interface Window {
    activeNotificationRef?: {
      userId: string | number;
      transferId?: number;
      important: boolean;
      urgent: boolean;
      read: boolean;
      collaborations: any[];
      assigned_by: string;
      date: Date;
    } | null;
    bulkHistoryCache?: {
      data: Array<{ id: string; phone: string; name: string; company_id: number }>;
      timestamp: number;
    } | null;
    bulkHistoryFetching?: boolean;
  }
}

const ChatSidebar = ({
  onSelectUser,
  usersWithHistory,
  loading,
  updateUsersWithHistory,
  conversationCounts,
  setConversationCounts,
  dateFilter,
  onDateFilterChange,
}: ChatSidebarProps) => {
  const { t } = useTranslation();
  const {
    myUsers,
    notifications,
    session,
    setIsTransferNotification,
    recentUsers,
    clearUnusedRecentUsers,
    setReturnControlToAi,
    setActivePhone,
    activePhone,
    activeNotification,
    setActiveNotification,
    isHistoryPage,
    setIsHistoryPage,
    setSelectedConversationId,
    setSelectedConversationLastDate,
    userInfo,
    allAssistants,
    setViewingNotifications,
    viewingNotifications,
    markMessagesSeen,
    company,
    setMyUsers,
  } = useSession();
  
  const [, startTransition] = useTransition();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [subView, setSubView] = useState<SubView>("new");
  const [filterKind, setFilterKind] = useState<FilterKind>("all");
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [isFetchingBulk, setIsFetchingBulk] = useState(false);
  const [usersCache, setUsersCache] = useState<{ [userId: string]: any }>({});
  const [bulkHistoryUsers, setBulkHistoryUsers] = useState<Array<{
    id: string;
    phone: string;
    name: string;
    company_id: number;
  }>>([]);
  const [filteredHistoryUsers, setFilteredHistoryUsers] = useState<Array<{
    id: string;
    phone: string;
    name: string;
    company_id: number;
  }>>([]);
  const [historyUsersLoaded, setHistoryUsersLoaded] = useState(false);
  const previousDateFilterRef = useRef<{ from?: string; to?: string; mode: string }>({ mode: "single" });
  const hasFetchedInitialHistory = useRef(false);

  const { getRelativeTime } = useRelativeTime(t);

  const { checkIfUserHasHistoryAndUpdate } = useUserHistory({
    myUsers,
    session,
    updateUsersWithHistory,
    dateFilter,
    updateConversationCounts: setConversationCounts,
  });

  const { suppressNextSelectRef, markTransferAsSeen } = useTransferActions({
    session,
    setReturnControlToAi,
    checkIfUserHasHistoryAndUpdate,
  });

  const myAssignedIds = useMemo(
    () =>
      Object.values(notifications?.transfers || {})
        .flat()
        .filter(
          (n: any) =>
            String(n.assigned_to || "") === String(session?.user?.id || "") &&
            !n.is_read &&
            !n.is_ignored,
        )
        .map((n: any) => Number(n.transferId)),
    [notifications?.transfers, session?.user?.id],
  );

  const handleSelectUserWithUserId = useCallback((phone: string, userId: string | number) => {
    onSelectUser(phone, userId);
  }, [onSelectUser]);

  const { handleNotificationClick, handleUserSelect } = useNotificationHandlers({
    activePhone,
    suppressNextSelectRef,
    myUsers,
    usersCache,
    setActiveNotification,
    setActivePhone,
    setSelectedConversationId,
    clearUnusedRecentUsers,
    setIsTransferNotification,
    onSelectUser: handleSelectUserWithUserId,
    session,
  });

  const pendingReselectionRef = useRef<{
    userId: string | number;
    transferId: number;
    targetSubView: SubView;
  } | null>(null);

  const handleNotificationClickPersist = (
    userId: string | number,
    phone: string,
    type: string,
    urgent: boolean,
    date: Date,
    important: boolean,
    ignored: boolean,
    read: boolean,
    collaborations: any[],
    assigned_by: string,
    transferId?: number,
  ) => {
    markMessagesSeen(userId);

    const isCurrentlyActive = 
      activePhone === phone && 
      activeNotification?.userId === userId &&
      activeNotification?.transferId === transferId;
    
    if (isCurrentlyActive) {
      setActiveNotification(null);
      setActivePhone(null);
      setSelectedConversationId(null);
      window.activeNotificationRef = null;
      return;
    }

    handleNotificationClick(
      userId,
      phone,
      type,
      urgent,
      date,
      important,
      ignored,
      read,
      collaborations,
      assigned_by,
      transferId,
    );

    if (type === "transfer" && transferId && subView === "new") {
      pendingReselectionRef.current = {
        userId,
        transferId,
        targetSubView: "in_process"
      };
      
      markTransferAsSeen(userId, transferId);
      setSubView("in_process");
    }
  };

  const { handleViewHistoryToggle } = useViewToggle({
    myUsers,
    activePhone,
    conversationCounts,
    setActivePhone,
    setViewingNotifications,
    setIsHistoryPage,
    setSelectedConversationId,
    setSelectedConversationLastDate,
    isHistoryPage,
    setActiveNotification,
  });

  const fetchAllUsersHistory = useCallback(async (applyDateFilter: boolean = false) => {
    if (window.bulkHistoryFetching) {
      return;
    }

    if (!applyDateFilter && window.bulkHistoryCache) {
      const cacheAge = Date.now() - window.bulkHistoryCache.timestamp;
      const CACHE_TTL = 5 * 60 * 1000; 
      
      if (cacheAge < CACHE_TTL) {
        setBulkHistoryUsers(window.bulkHistoryCache.data);
        setHistoryUsersLoaded(true);
        setIsFetchingBulk(false);

        if (window.bulkHistoryCache.data.length > 0 && myUsers.length === 0) {
          setMyUsers(window.bulkHistoryCache.data);
        }
        return;
      }
    }

    window.bulkHistoryFetching = true;
    setIsFetchingBulk(true);
    setHistoryUsersLoaded(false);

    try {
      let fetchParams: any = {
        session,
      };

      if (applyDateFilter && dateFilter && (dateFilter.from || dateFilter.to)) {
        if (dateFilter.mode === "single" && dateFilter.from) {
          fetchParams.from = dateFilter.from;
          fetchParams.to = dateFilter.from;
        } else if (dateFilter.mode === "range") {
          if (dateFilter.from) fetchParams.from = dateFilter.from;
          if (dateFilter.to) fetchParams.to = dateFilter.to;
        }
      }

      const usersWithHistoryData = await getBulkMessageHistory(fetchParams) as Array<{
        id: string;
        phone: string;
        name: string;
        company_id: number;
      }>;

      if (applyDateFilter) {
        setFilteredHistoryUsers(usersWithHistoryData || []);
      } else {
        setBulkHistoryUsers(usersWithHistoryData || []);
        setFilteredHistoryUsers([]);
        
        window.bulkHistoryCache = {
          data: usersWithHistoryData || [],
          timestamp: Date.now()
        };

        if (usersWithHistoryData && usersWithHistoryData.length > 0) {
          setMyUsers(usersWithHistoryData);
        }
      }
      
      setHistoryUsersLoaded(true);
      setIsFetchingBulk(false);      
    } catch (error) {
      console.error("❌ Error fetching bulk history:", error);
      if (applyDateFilter) {
        setFilteredHistoryUsers([]);
      } else {
        setBulkHistoryUsers([]);
      }
            setHistoryUsersLoaded(true);
      setIsFetchingBulk(false);      
    } finally {
      window.bulkHistoryFetching = false;
    }
  }, [session, dateFilter, myUsers.length, setMyUsers]);

  useEffect(() => {
    if (!session || !userInfo) {
      return;
    }
    
    if (!viewingNotifications) {
      return;
    }
    
    if (hasFetchedInitialHistory.current) {
      return;
    }
    
    if (window.bulkHistoryFetching) {
      return;
    }
    
    hasFetchedInitialHistory.current = true;
    fetchAllUsersHistory(false);
  }, [session, userInfo, viewingNotifications, fetchAllUsersHistory]);

  useEffect(() => {
    if (!viewingNotifications || !session) return;
    
    if (window.bulkHistoryCache && bulkHistoryUsers.length === 0 && !isFetchingBulk && historyUsersLoaded) {
      const cacheAge = Date.now() - window.bulkHistoryCache.timestamp;
      const CACHE_TTL = 5 * 60 * 1000; 
      
      if (cacheAge < CACHE_TTL) {
        setBulkHistoryUsers(window.bulkHistoryCache.data);
      }
    }
  }, [viewingNotifications, bulkHistoryUsers.length, isFetchingBulk, session, historyUsersLoaded]);

  useEffect(() => {
    if (!isHistoryPage || filterKind !== "date") return;

    const currentFilter = {
      from: dateFilter.from,
      to: dateFilter.to,
      mode: dateFilter.mode
    };

    const previousFilter = previousDateFilterRef.current;

    const hasChanged = 
      currentFilter.from !== previousFilter.from ||
      currentFilter.to !== previousFilter.to ||
      currentFilter.mode !== previousFilter.mode;

    if (hasChanged && (dateFilter.from || dateFilter.to)) {
      previousDateFilterRef.current = currentFilter;
      updateUsersWithHistory(new Set());
      fetchAllUsersHistory(true);
    }
  }, [dateFilter.from, dateFilter.to, dateFilter.mode, isHistoryPage, filterKind, fetchAllUsersHistory, updateUsersWithHistory]);

  useEffect(() => {
    if (isHistoryPage && filterKind !== "date" && filteredHistoryUsers.length > 0) {
      setFilteredHistoryUsers([]);
    }
  }, [isHistoryPage, filterKind]);

  const { urgentTransfers, regularNotifications } = useNotificationData({
    notifications,
    myAssignedIds,
    myUsers,
    t,
  });

  const usersForHistoryList = useMemo(() => {
    if (!isHistoryPage) return [];
    

    if (filterKind === "date" && (dateFilter.from || dateFilter.to)) {
      return filteredHistoryUsers;
    }
    
    return bulkHistoryUsers;
  }, [isHistoryPage, filterKind, dateFilter.from, dateFilter.to, filteredHistoryUsers, bulkHistoryUsers]);

  const { filteredAndSortedUsers } = useUserFiltering({
    myUsers: usersForHistoryList, 
    historyUsers: usersForHistoryList,
    usersWithHistory: new Set(usersForHistoryList.map(u => String(u.id))),
    searchQuery,
    sortOrder,
    isHistoryPage,
  });

  useNotificationEffects({ notifications, updateUsersWithHistory });

  const latestByUser = useCallback((items: any[]) => {
    const map = new Map<string, any>();
    for (const it of items) {
      const key = String(it.userId);
      const ts = new Date(it.date || new Date().toISOString()).getTime();
      const prev = map.get(key);
      if (!prev || ts > new Date(prev.date || new Date().toISOString()).getTime()) {
        map.set(key, it);
      }
    }
    return Array.from(map.values());
  }, []);

  const allTransfersFlat = useMemo(() => {
    const out: any[] = [];
    const entries = Object.entries(notifications?.transfers || {}) as Array<[string, any[]]>;
    for (const [userId, transfers] of entries) {
      for (const tr of transfers || []) {
        out.push({
          userId: userId,
          transferId: tr.transferId,
          type: "transfer",
          content: tr.question ?? "",
          date: tr.date || new Date().toISOString(),
          urgent: Boolean(tr.is_urgent),
          read: Boolean(tr.is_read),
          open: Boolean(tr.is_open),
          important: Boolean(tr.is_important),
          ignored: Boolean(tr.is_ignored),
          collaborations: tr.collaborations,
          assigned_by: tr.assigned_by,
          assigned_to: tr.assigned_to ?? null,
          newCount: 0,
        });
      }
    }
    return out;
  }, [notifications?.transfers]);

  const newTransfers = useMemo(
    () => latestByUser(allTransfersFlat.filter((tr) => !tr.read && !tr.open)),
    [allTransfersFlat, latestByUser],
  );

  const inProcessTransfers = useMemo(
    () => latestByUser(allTransfersFlat.filter((tr) => !tr.read && tr.open)),
    [allTransfersFlat, latestByUser],
  );

  const newCount = newTransfers.length;
  const inProcessCount = inProcessTransfers.length;
  const agentCount = urgentTransfers.length + regularNotifications.length;

  const displayedUrgent = useMemo(() => {
    if (subView === "agent") return urgentTransfers;
    const base = subView === "new" ? newTransfers : inProcessTransfers;
    return base.filter((n: any) => n.urgent);
  }, [subView, urgentTransfers, newTransfers, inProcessTransfers]);

  const displayedRegular = useMemo(() => {
    if (subView === "agent") return regularNotifications;
    const base = subView === "new" ? newTransfers : inProcessTransfers;
    return base.filter((n: any) => !n.urgent);
  }, [subView, regularNotifications, newTransfers, inProcessTransfers]);

  const showUrgent = subView === "agent" ? urgentTransfers : displayedUrgent;
  const showRegular = subView === "agent" ? regularNotifications : displayedRegular;

  useEffect(() => {
    if (!pendingReselectionRef.current || subView !== pendingReselectionRef.current.targetSubView) {
      return;
    }

    const { userId, transferId } = pendingReselectionRef.current;
    
    const attemptReselection = (attempt = 1, maxAttempts = 10) => {
      const allCurrentNotifications = [...showUrgent, ...showRegular];
      const targetNotification = allCurrentNotifications.find(n => 
        String(n.userId) === String(userId) && 
        Number(n.transferId) === Number(transferId)
      );
      
      if (targetNotification) {
        const cachedUser = usersCache[String(userId)];
        const user = cachedUser || myUsers.find((u) => String(u.id) === String(userId));
        
        if (user?.phone) {
          handleNotificationClick(
            targetNotification.userId,
            user.phone,
            targetNotification.type,
            targetNotification.urgent,
            targetNotification.date,
            targetNotification.important,
            targetNotification.ignored,
            targetNotification.read,
            targetNotification.collaborations,
            targetNotification.assigned_by,
            targetNotification.transferId
          );
          
          pendingReselectionRef.current = null;
          return;
        }
      }
      
      if (attempt < maxAttempts) {
        setTimeout(() => {
          attemptReselection(attempt + 1, maxAttempts);
        }, 300);
      } else {
        pendingReselectionRef.current = null;
      }
    };

    const timeoutId = setTimeout(() => {
      attemptReselection();
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [subView, showUrgent, showRegular, usersCache, myUsers, handleNotificationClick]);

  useEffect(() => {
    setActiveNotification(null);
    setActivePhone(null);
    setSelectedConversationId(null);

    if (subView === "agent") {
      const firstNotification = showUrgent[0] || showRegular[0];
      if (firstNotification) {
        const cachedUser = usersCache[String(firstNotification.userId)];
        const user = cachedUser || myUsers.find((u) => String(u.id) === String(firstNotification.userId));
        
        if (user?.phone) {
          handleNotificationClickPersist(
            firstNotification.userId,
            user.phone,
            firstNotification.type,
            firstNotification.urgent,
            firstNotification.date,
            firstNotification.important,
            firstNotification.ignored,
            firstNotification.read,
            firstNotification.collaborations,
            firstNotification.assigned_by,
            firstNotification.transferId
          );
        }
      }
    }
  }, [subView, setActiveNotification, setActivePhone, setSelectedConversationId, usersCache]);

  useEffect(() => {
    if (!isHistoryPage) return;
    if (filterKind !== "date") return;

    if (dateFilter.from || dateFilter.to) {
      updateUsersWithHistory(new Set());
    }
  }, [isHistoryPage, filterKind, updateUsersWithHistory]);

  useEffect(() => {
    window.activeNotificationRef = null;
    
    return () => {
      window.activeNotificationRef = null;
    };
  }, []);

  const usersVisibleByDate = useMemo(() => {
    if (!isHistoryPage) return [];
    return bulkHistoryUsers;
  }, [bulkHistoryUsers, isHistoryPage]);

  const usersForList = useMemo(() => {
    if (isHistoryPage) {
      return filteredAndSortedUsers;
    }
    return [];
  }, [filteredAndSortedUsers, isHistoryPage]);

  const isDateFilterApplied = useMemo(() => {
    return Boolean(isHistoryPage && filterKind === "date" && (dateFilter.from || dateFilter.to));
  }, [isHistoryPage, filterKind, dateFilter]);

  const abbreviation = getAgentAbbreviation(userInfo?.name, true);
  return (
    <Sidebar className="w-full md:w-[17.75rem] h-full bg-white">
      <SidebarToggleHeader
        isHistoryPage={isHistoryPage}
        onToggle={() => {
          startTransition(() => {
            handleViewHistoryToggle();
          });
        }}
        t={t}
        isPending={loading || isFetchingBulk}
      />

      {!viewingNotifications && (
        <SidebarFilters
          searchQuery={searchQuery}
          sortOrder={sortOrder}
          onSearchQueryChange={setSearchQuery}
          onSortOrderChange={setSortOrder}
          filterKind={filterKind}
          onFilterKindChange={setFilterKind}
          assistants={allAssistants}
          companyId={company?.id}
          selectedAgentId={selectedAgentId}
          onAgentSelect={setSelectedAgentId}
          dateFilter={dateFilter}
          onDateFilterChange={onDateFilterChange}
          t={t}
          isHistoryPage={isHistoryPage}
        />
      )}

      {viewingNotifications && !(loading || isFetchingBulk) &&(
        <SidebarSubViewBar
          subView={subView}
          setSubView={setSubView}
          newCount={newCount}
          inProcessCount={inProcessCount}
          agentCount={agentCount}
          agentLabel={abbreviation}
          t={t}
        />
      )}

      <SidebarContent>
        <div className="flex flex-col h-full">
          <ScrollArea className="flex-grow overflow-y-auto h-full">
            <div className="h-full">
              {(loading || isFetchingBulk) ? (
                <div className="flex flex-col items-center justify-center h-32 px-4">
                  <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
                  <h3 className="text-base font-medium text-gray-700 mb-1">
                    {isHistoryPage && filterKind === "date" 
                      ? t("loading_history")
                      : t("loading_notifications")}
                  </h3>
                  <p className="text-sm text-gray-500 text-center">
                    {isHistoryPage && filterKind === "date"
                      ? t("please_wait_history")
                      : t("please_wait_notifications")}
                  </p>
                </div>
              ) : viewingNotifications ? (
                <>
                  <RecentUsersList recentUsers={recentUsers} t={t} />
                  <NotificationsList
                    urgentTransfers={showUrgent}
                    regularNotifications={showRegular}
                    myUsers={myUsers}
                    activePhone={activePhone}
                    activeNotification={activeNotification}
                    onNotificationClick={handleNotificationClickPersist}
                    getRelativeTime={getRelativeTime}
                    assistants={allAssistants}
                    usersCache={usersCache}
                    setUsersCache={setUsersCache}
                    subView={subView}
                  />
                </>
              ) : (
                <UserHistoryList
                  loading={false}
                  filteredAndSortedUsers={usersForList}
                  activePhone={activePhone}
                  conversationCounts={conversationCounts}
                  onUserSelect={handleUserSelect}
                  t={t}
                  isDateFiltered={isDateFilterApplied}
                />
              )}
            </div>
          </ScrollArea>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};

export default ChatSidebar;
